import * as React from 'react';
import { CampaignWithNodeDetails } from '../services/campaign/types';
import { QRCodeSVG } from 'qrcode.react';

// Typography utility functions for consistent styling across previews
const getTypographyStyle = (
  elementType: string,
  typography: any,
  scale: number = 1,
  brandColors?: { primary: string; secondary: string; accent: string }
) => {
  const typePrefix = elementType === 'benefit' ? 'benefit' : elementType;
  
  // Get default color based on element type and brand colors
  const getDefaultColor = () => {
    if (!brandColors) return '#000000';
    
    switch (elementType) {
      case 'headline':
      case 'benefit': 
      case 'description':
        return brandColors.secondary; // Use secondary brand color for all text elements
      case 'cta':
        return '#FFFFFF'; // Keep white for CTA button text for readability
      default:
        return '#000000';
    }
  };
  
  // Use individual color if set, otherwise use brand color defaults
  const effectiveColor = typography[`${typePrefix}Color`] || getDefaultColor();
  
  return {
    color: effectiveColor,
    fontSize: `${Math.max(8, (typography[`${typePrefix}FontSize`] || 16) * scale)}px`,
    fontFamily: typography.selectedFont || 'Inter, sans-serif',
    fontWeight: typography[`${typePrefix}Bold`] ? 'bold' : 'normal',
    fontStyle: typography[`${typePrefix}Italic`] ? 'italic' : 'normal',
    textAlign: typography[`${typePrefix}Align`] || 'center',
    textShadow: typography[`${typePrefix}Shadow`] ? '1px 1px 2px rgba(0,0,0,0.5)' : 'none'
  };
};

// Build final URL with tracking parameters
const buildFinalUrlFromCampaign = (campaign: CampaignWithNodeDetails) => {
  const qrData = campaign.goals?.slot_b_details;
  const baseUrl = qrData?.destination_url || campaign.destination_url || campaign.qr_url;
  
  if (!baseUrl?.trim()) return '';

  // Note: Tracking parameters have been deprecated
  // For Dynamic QR, add QR ID for campaign tracking
  if (campaign.qr_type === 'Dynamic QR' && campaign.qr_id) {
    try {
      const url = new URL(baseUrl.trim());
      url.searchParams.set('qr_id', campaign.qr_id);
      url.searchParams.set('qr_type', 'dynamic');
      return url.toString();
    } catch (error) {
      console.error('Error building QR tracking URL:', error);
      return baseUrl.trim();
    }
  }

  return baseUrl.trim();
};

// Reconstruct savedLayoutData structure from campaign data
const reconstructLayoutData = (campaign: CampaignWithNodeDetails) => {
  // Priority 1: Use direct database fields saved from CampaignBuilder step 7
  // Priority 2: Fall back to goals.slot_a_details for legacy data
  const slotData = campaign.goals?.slot_a_details;
  
  return {
    layout: campaign.goals?.slot_a_details?.layout_style || (campaign as any).selectedLayoutStyle || 'vertical-flow',
    // Use the final rendered ad image with typography (ad_image_url) as primary source
    image: campaign.ad_image_url || slotData?.preview_image_url || slotData?.image_url,
    logo: slotData?.logo_url || '',
    typography: {
      // Read from direct database fields first (saved from CampaignBuilder)
      selectedFont: (campaign as any).selected_font || slotData?.typography?.font_family || 'Inter, sans-serif',
      headlineFontSize: (campaign as any).headline_font_size || slotData?.typography?.headline?.size || 24,
      headlineColor: (campaign as any).headline_color || slotData?.typography?.headline?.color || '#FFFFFF',
      headlineAlign: (campaign as any).headline_align || slotData?.typography?.headline?.align || 'center',
      headlineBold: (campaign as any).headline_bold || slotData?.typography?.headline?.bold || false,
      headlineItalic: (campaign as any).headline_italic || slotData?.typography?.headline?.italic || false,
      headlineShadow: (campaign as any).headline_shadow || slotData?.typography?.headline?.shadow || false,
      benefitFontSize: (campaign as any).benefit_font_size || slotData?.typography?.benefit?.size || 18,
      benefitColor: (campaign as any).benefit_color || slotData?.typography?.benefit?.color || '#059669',
      benefitAlign: (campaign as any).benefit_align || slotData?.typography?.benefit?.align || 'center',
      benefitBold: (campaign as any).benefit_bold || slotData?.typography?.benefit?.bold || false,
      benefitItalic: (campaign as any).benefit_italic || slotData?.typography?.benefit?.italic || false,
      benefitShadow: (campaign as any).benefit_shadow || slotData?.typography?.benefit?.shadow || false,
      descriptionFontSize: (campaign as any).description_font_size || slotData?.typography?.description?.size || 16,
      descriptionColor: (campaign as any).description_color || slotData?.typography?.description?.color || '#E5E7EB',
      descriptionAlign: (campaign as any).description_align || slotData?.typography?.description?.align || 'center',
      descriptionBold: (campaign as any).description_bold || slotData?.typography?.description?.bold || false,
      descriptionItalic: (campaign as any).description_italic || slotData?.typography?.description?.italic || false,
      descriptionShadow: (campaign as any).description_shadow || slotData?.typography?.description?.shadow || false,
      ctaFontSize: (campaign as any).cta_font_size || slotData?.typography?.cta?.size || 16,
      ctaColor: (campaign as any).cta_color || slotData?.typography?.cta?.color || '#FFFFFF',
      ctaAlign: (campaign as any).cta_align || slotData?.typography?.cta?.align || 'center',
      ctaBold: (campaign as any).cta_bold || slotData?.typography?.cta?.bold || true,
      ctaItalic: (campaign as any).cta_italic || slotData?.typography?.cta?.italic || false,
      ctaShadow: (campaign as any).cta_shadow || slotData?.typography?.cta?.shadow || false,
    },
    textContent: {
      // Read from direct database fields first
      headline: campaign.headline || slotData?.headline || 'Your Headline Here',
      description: campaign.description || slotData?.description || 'Your description here',
      benefit: campaign.benefit || slotData?.benefit || '',
      callToAction: campaign.call_to_action || slotData?.call_to_action || campaign.cta || 'Take Action'
    },
    colors: {
      primary: slotData?.primary_color || '#0066FF',
      secondary: slotData?.secondary_color || '#FFFFFF',
      accent: slotData?.accent_color || '#3B82F6'
    },
    // Add overlay settings for overlays
    overlaySettings: (campaign as any).overlay_settings || {},
    overlayImage: (campaign as any).overlay_image_url || null
  };
};

const renderCtaIcon = (iconType: string, className: string = "w-5 h-5") => {
  switch (iconType) {
    case 'shopping-cart':
      return React.createElement(
        'svg',
        { className, fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' },
        React.createElement('path', {
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          strokeWidth: 2,
          d: 'M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-1.1 5.5M7 13v6a2 2 0 002 2h6a2 2 0 002-2v-6M9 19h6'
        })
      );
    case 'phone':
      return React.createElement(
        'svg',
        { className, fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' },
        React.createElement('path', {
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          strokeWidth: 2,
          d: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2H7a2 2 0 01-2-2V5z'
        })
      );
    case 'envelope':
      return React.createElement(
        'svg',
        { className, fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' },
        React.createElement('path', {
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          strokeWidth: 2,
          d: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z'
        })
      );
    case 'download':
      return React.createElement(
        'svg',
        { className, fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' },
        React.createElement('path', {
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          strokeWidth: 2,
          d: 'M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z'
        })
      );
    case 'heart':
      return React.createElement(
        'svg',
        { className, fill: 'none', stroke: 'currentColor', viewBox: '0 0 24 24' },
        React.createElement('path', {
          strokeLinecap: 'round',
          strokeLinejoin: 'round',
          strokeWidth: 2,
          d: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z'
        })
      );
    default:
      return null;
  }
};

// Utility function for formatting dates consistently
const formatDate = (dateString: string | null | undefined) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString).toLocaleDateString('en-US', { 
      weekday: 'short', month: 'short', day: 'numeric' 
    });
  } catch (e) {
    return 'N/A';
  }
};

// Format date range with days count
const formatDateRange = (startDate: string | null | undefined, endDate: string | null | undefined) => {
  if (startDate && endDate) {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = Math.floor((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const startFormatted = formatDate(startDate);
    const endFormatted = formatDate(endDate);
    return `${startFormatted} - ${endFormatted} (${days} days)`;
  } else if (startDate) {
    return `${formatDate(startDate)} - N/A`;
  } else if (endDate) {
    return `N/A - ${formatDate(endDate)}`;
  }
  return 'N/A to N/A';
};

export {
  getTypographyStyle,
  buildFinalUrlFromCampaign,
  reconstructLayoutData,
  renderCtaIcon,
  formatDate,
  formatDateRange
};